import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import Button from './Button';
import Section from './Section';
import type { AboutTrailerHighlight } from '../lib/types';

interface SectionTrailerProps {
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  mediaType?: 'image' | 'video';
  mediaUrl?: string;
  reverse?: boolean;
  highlights?: AboutTrailerHighlight[];
}

const SectionTrailer: React.FC<SectionTrailerProps> = ({
  title,
  description,
  ctaText,
  ctaLink,
  mediaType,
  mediaUrl,
  reverse = false,
  highlights,
}) => {
  const iconMap = Icons as unknown as Record<string, React.ComponentType<any>>;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (mediaType === 'video' && videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, [mediaType, mediaUrl]);

  return (
    <Section padding="default">
      <div
        className={`grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center ${
          reverse ? 'lg:grid-flow-dense' : ''
        }`}
      >
        <motion.div
          initial={{ opacity: 0, x: reverse ? 20 : -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className={reverse ? 'lg:col-start-2' : ''}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-5 sm:mb-6">
            {title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8">
            {description}
          </p>

          {highlights && highlights.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {highlights.map((highlight, index) => {
                const IconComponent = iconMap[highlight.icon] || Icons.CheckCircle;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <IconComponent className="w-6 h-6 text-primary-red flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-playfair font-semibold text-lg text-primary-black mb-1">
                        {highlight.title}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Button variant="primary" href={ctaLink}>
            {ctaText}
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: reverse ? -20 : 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={reverse ? 'lg:col-start-1 lg:row-start-1' : ''}
        >
          {mediaType === 'video' ? (
            <div className="relative rounded-xl overflow-hidden shadow-lg aspect-video bg-black">
              <video
                ref={videoRef}
                key={mediaUrl}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedData={(e) => {
                  const video = e.currentTarget;
                  video.play().catch(err => console.log('Video play error:', err));
                }}
                className="w-full h-full object-cover"
              >
                <source src={mediaUrl} type="video/mp4" />
              </video>
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden shadow-lg aspect-video">
              <img
                src={mediaUrl}
                alt={title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
        </motion.div>
      </div>
    </Section>
  );
};

export default SectionTrailer;
